/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ApiService, AuthStoreService } from '../../../../core/services';
import { ClusterPairingEnum, NodesEnum } from '../enums/api';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { ModulesEnum } from '../../../../core/enums';
import { NewClusterModel } from '../models';
import { Observable } from 'rxjs';

@Injectable()
export class ClusterApiService {
    constructor(
        private api: ApiService,
        private authStoreService: AuthStoreService
    ) {
    }

    /**
     * Get the cluster status for the connected appliance.
     * @returns {Observable<any>}
     */
    public getStatus(options?: any): Observable<any> {
        return new Observable(observer => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
            this.api.get(NodesEnum.GET_NODE_STATUS, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Sends a pairing request via the api
     * @returns {Observable<any>}
     */
    public pair(cluster: NewClusterModel, options?: any): Observable<any> {
        return new Observable(observer => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);
            this.api.post(ClusterPairingEnum.POST_CLUSTER_MASTER, cluster, moduleAuth, false, options)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Sends the break cluster request via the api
     * @returns {Observable<any>}
     */
    public removeNode(id: any, options?: any): Observable<any> {
        return new Observable(observer => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.delete(ClusterPairingEnum.DELETE_CLUSTER(id), moduleAuth, false, options)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Sends the cluster resync request via the api
     * @returns {Observable<any>}
     */
    public resync(options?: any): Observable<any> {
        return new Observable(observer => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.put(ClusterPairingEnum.PUT_CLUSTER_RESYNC, null, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Sends the cluster apply request via the api
     * @returns {Observable<any>}
     */
    public apply(options?: any): Observable<any> {
        return new Observable(observer => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.put(ClusterPairingEnum.PUT_CLUSTER_APPLY, null, moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Sends the cluster promote a node request via the api
     * @returns {Observable<any>}
     */
    public promote(id: any, options?: any): Observable<any> {
        return new Observable(observer => {
            // Get our selected module details
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.get(ClusterPairingEnum.GET_PROMOTE(id), moduleAuth)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Restore unavailable node
     * @param cluster
     * @param options
     */
    public restoreNode(cluster: NewClusterModel, options?: any): Observable<any> {
        return new Observable(observer => {
            const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

            this.api.post(ClusterPairingEnum.POST_RESTORE_NODE, cluster, moduleAuth, false, options)
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }
}
